/**
 * ForksTree — tree visualisation of all forks descended from a
 * marketplace listing. Renders the parent at the root and every fork
 * as an indented child node with creator + revenue + timestamp.
 *
 * Royalty decay (geometric, 10× per generation):
 *   Original author → 88%
 *   Fork-1 author   → 8.8%   (parent earns 88%, fork earns 8.8%, …)
 *   Fork-2 author   → 0.88%
 *
 * The decay is an enforcement detail — gi-royalty applies it at
 * settlement time, not at fork registration. Visualising it here
 * gives creators an honest preview of "what would I take home if I
 * fork this product".
 *
 * Today the data is mocked (per-listing arrays in `mockForks`); the
 * shape is what the real `/api/v1/marketplace/listings/{id}/forks`
 * endpoint will return when it lands in Phase 4.9.
 */

import { useMemo } from 'react';

import { Card, CardSubtitle, CardTitle } from '@/components/Card';
import { useLocaleStore } from '@/stores/localeStore';
import { formatNumber } from '@/utils/format';
import type { EntityId } from '@/api/types';

const ACCENT = '#6366f1';
const ROYALTY_AUTHOR_BPS = 8_800; // 88.00 % in basis points
const ROYALTY_DECAY_PER_GEN = 10; // 10× decay per generation

export interface ForkNode {
  id: string;
  name: string;
  creator: string;
  /** ISO 8601. */
  forked_at: string;
  /** USD revenue this fork has earned cumulatively. */
  revenue_usd: number;
  /** Generation depth from the original (1 = direct fork). */
  generation: number;
  children: ForkNode[];
}

/** Mock per-listing fork tree. Real wiring lands in Phase 4.9. */
const MOCK_FORKS: Record<string, ForkNode[]> = {
  '00000000-0000-7100-9100-000000000001': [ // Code Proof
    {
      id: 'fork_cp_1',
      name: 'GitLab Push Receipts',
      creator: 'Maya Eilon',
      forked_at: '2026-03-12T00:00:00Z',
      revenue_usd: 4_280,
      generation: 1,
      children: [
        {
          id: 'fork_cp_1_1',
          name: 'Self-Hosted GitLab + SLSA',
          creator: 'Aharon Tzvi',
          forked_at: '2026-04-02T00:00:00Z',
          revenue_usd: 940,
          generation: 2,
          children: [],
        },
      ],
    },
    {
      id: 'fork_cp_2',
      name: 'Bitbucket Release Proofs',
      creator: 'Ola Becker',
      forked_at: '2026-03-28T00:00:00Z',
      revenue_usd: 1_640,
      generation: 1,
      children: [],
    },
  ],
  '00000000-0000-7100-9100-000000000003': [ // Freelance Contract
    {
      id: 'fork_fc_1',
      name: 'Agency Engagement Bundle',
      creator: 'Yael Sade',
      forked_at: '2026-02-22T00:00:00Z',
      revenue_usd: 6_120,
      generation: 1,
      children: [
        {
          id: 'fork_fc_1_1',
          name: 'Multi-Currency Agency Bundle',
          creator: 'Pedro Lima',
          forked_at: '2026-04-09T00:00:00Z',
          revenue_usd: 1_580,
          generation: 2,
          children: [
            {
              id: 'fork_fc_1_1_1',
              name: 'Multi-Currency + Crypto',
              creator: 'Vinh Tran',
              forked_at: '2026-04-22T00:00:00Z',
              revenue_usd: 220,
              generation: 3,
              children: [],
            },
          ],
        },
      ],
    },
  ],
  '00000000-0000-7100-9100-000000000004': [ // Beauty Protocol
    {
      id: 'fork_bp_1',
      name: 'Skincare Studio Protocol',
      creator: 'Mira Avraham',
      forked_at: '2026-04-01T00:00:00Z',
      revenue_usd: 990,
      generation: 1,
      children: [],
    },
  ],
};

interface ForksTreeProps {
  listing_root_id: EntityId;
  parent_name: string;
  parent_creator: string;
  parent_revenue_usd: number;
  /** Click handler for the "Fork this product" CTA. */
  onFork: () => void;
}

export function ForksTree({
  listing_root_id,
  parent_name,
  parent_creator,
  parent_revenue_usd,
  onFork,
}: ForksTreeProps) {
  const locale = useLocaleStore((s) => s.locale);
  const forks = useMemo<ForkNode[]>(
    () => MOCK_FORKS[listing_root_id] ?? [],
    [listing_root_id],
  );

  const totals = useMemo(() => flatten(forks), [forks]);

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Forks</CardTitle>
            <CardSubtitle>
              Every product forked from the original carries a geometric
              royalty back to ancestors (10× decay per generation, capped at
              gen 5).
            </CardSubtitle>
          </div>
          <button
            type="button"
            onClick={onFork}
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
            style={{ backgroundColor: ACCENT }}
          >
            <span aria-hidden>⑂</span>
            Fork this product
          </button>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Direct forks" value={String(forks.length)} />
          <Stat label="Total descendants" value={String(totals.count)} />
          <Stat
            label="Fork revenue (all gens)"
            value={`$${formatNumber(totals.revenue, locale)}`}
          />
          <Stat
            label="Royalties to original"
            value={`$${formatNumber(
              Math.round(estimateRoyaltyToOriginal(forks)),
              locale,
            )}`}
          />
        </div>
      </Card>

      {/* Royalty decay legend */}
      <Card>
        <CardTitle>Royalty chain</CardTitle>
        <CardSubtitle>
          Per-sale split, geometric decay. Author gets {(ROYALTY_AUTHOR_BPS / 100).toFixed(0)}% of each sale; each
          downstream generation earns {(ROYALTY_AUTHOR_BPS / 100 / ROYALTY_DECAY_PER_GEN).toFixed(2)}% of its
          parent's share.
        </CardSubtitle>
        <ol className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-5">
          {[0, 1, 2, 3, 4].map((gen) => {
            const pct = ROYALTY_AUTHOR_BPS / Math.pow(ROYALTY_DECAY_PER_GEN, gen) / 100;
            return (
              <li
                key={gen}
                className="flex flex-col gap-1 rounded-lg border border-border bg-canvas p-3"
              >
                <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">
                  {gen === 0 ? 'Original' : `Gen ${gen}`}
                </span>
                <span className="text-xl font-semibold tabular-nums" style={{ color: ACCENT }}>
                  {pct >= 0.01 ? `${pct.toFixed(2)}%` : '<0.01%'}
                </span>
              </li>
            );
          })}
        </ol>
      </Card>

      {/* Tree */}
      {forks.length === 0 ? (
        <Card>
          <CardTitle>No forks yet</CardTitle>
          <CardSubtitle>
            Be the first. The "Fork this product" button starts a Spark with
            this listing as its parent — the lineage is signed and the royalty
            chain locks in at publish time.
          </CardSubtitle>
        </Card>
      ) : (
        <Card>
          <CardTitle>Fork tree</CardTitle>
          <CardSubtitle>
            Indentation = generation. Hover any node for its full handle.
          </CardSubtitle>
          <div className="mt-4">
            <ParentRow
              name={parent_name}
              creator={parent_creator}
              revenue_usd={parent_revenue_usd}
            />
            <ul className="mt-3 flex flex-col gap-2">
              {forks.map((f) => (
                <ForkBranch key={f.id} node={f} />
              ))}
            </ul>
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
        {label}
      </span>
      <span className="text-xl font-semibold text-ink">{value}</span>
    </div>
  );
}

function ParentRow({
  name,
  creator,
  revenue_usd,
}: {
  name: string;
  creator: string;
  revenue_usd: number;
}) {
  const locale = useLocaleStore((s) => s.locale);
  return (
    <div
      className="flex items-center justify-between gap-3 rounded-lg p-3"
      style={{ backgroundColor: '#eef2ff', border: `1px solid #c7d2fe` }}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span
          className="flex h-8 w-8 flex-none items-center justify-center rounded-full text-base"
          style={{ backgroundColor: ACCENT, color: 'white' }}
          aria-hidden
        >
          ★
        </span>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-ink">{name}</div>
          <div className="text-[11px] text-ink-subtle">
            Original by {creator}
          </div>
        </div>
      </div>
      <div className="text-end">
        <div className="text-xs text-ink-subtle">Original revenue</div>
        <div className="text-sm font-semibold text-ink">
          ${formatNumber(revenue_usd, locale)}
        </div>
      </div>
    </div>
  );
}

function ForkBranch({ node }: { node: ForkNode }) {
  const locale = useLocaleStore((s) => s.locale);
  const indent = Math.min(node.generation - 1, 4) * 24;

  return (
    <li>
      <div
        className="flex items-start gap-3 rounded-lg border border-border bg-surface p-3"
        style={{ marginInlineStart: indent }}
      >
        <div
          className="flex h-7 w-7 flex-none items-center justify-center rounded-full text-xs font-semibold"
          style={{
            backgroundColor: '#eef2ff',
            color: ACCENT,
          }}
          aria-hidden
        >
          {node.generation}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="truncate text-sm font-semibold text-ink">
              {node.name}
            </span>
            <span className="text-[11px] text-ink-subtle">
              by {node.creator}
            </span>
          </div>
          <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-ink-subtle">
            <span>
              Forked{' '}
              {new Date(node.forked_at).toLocaleDateString(locale, {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
              })}
            </span>
            <span>
              ${formatNumber(node.revenue_usd, locale)} revenue
            </span>
            <span>
              Royalty to original ≈ $
              {formatNumber(
                Math.round(
                  (node.revenue_usd * ROYALTY_AUTHOR_BPS) /
                    Math.pow(ROYALTY_DECAY_PER_GEN, node.generation) /
                    10_000,
                ),
                locale,
              )}
            </span>
          </div>
        </div>
      </div>
      {node.children.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-2">
          {node.children.map((child) => (
            <ForkBranch key={child.id} node={child} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────

function flatten(nodes: ForkNode[]): { count: number; revenue: number } {
  let count = 0;
  let revenue = 0;
  const walk = (ns: ForkNode[]): void => {
    for (const n of ns) {
      count += 1;
      revenue += n.revenue_usd;
      walk(n.children);
    }
  };
  walk(nodes);
  return { count, revenue };
}

function estimateRoyaltyToOriginal(nodes: ForkNode[]): number {
  // Each fork sends `(parent.revenue * 88% / 10^gen)` upstream.
  let total = 0;
  const walk = (ns: ForkNode[]): void => {
    for (const n of ns) {
      total +=
        (n.revenue_usd * ROYALTY_AUTHOR_BPS) /
        Math.pow(ROYALTY_DECAY_PER_GEN, n.generation) /
        10_000;
      walk(n.children);
    }
  };
  walk(nodes);
  return total;
}
